import React, { useEffect, useState } from 'react';
import { Col, Form, Input, Row, Select, Typography, InputNumber, DatePicker } from 'antd';
import type { FormInstance } from 'antd';
import { useOfferingCategoryOptions } from '../../../hooks/options/useOfferingCategoryOptions';
import type { OfferingDetail, OfferingFormValues, OfferingStatus, DiscountType } from '../types';
import dayjs from 'dayjs';

const { TextArea } = Input;
const { Title } = Typography;

export interface OfferingFormProps {
  form: FormInstance<OfferingFormFieldValues>;
  initialValues?: OfferingDetail | null;
  onFinish: (values: OfferingFormValues) => void;
}

const statusOptions: { value: OfferingStatus; label: string }[] = [
  { value: 0, label: 'Active' },
  { value: 1, label: 'Deactivated' },
];

const discountTypeOptions: { value: DiscountType; label: string }[] = [
  { value: 0, label: 'Flat Amount' },
  { value: 1, label: 'Percentage' },
];

export interface OfferingFormFieldValues extends Omit<OfferingFormValues, 'discountStart' | 'discountEnd'> {
  discountPeriod?: [dayjs.Dayjs, dayjs.Dayjs];
}

function offeringDetailToFormValues(d: OfferingDetail): OfferingFormFieldValues {
  return {
    name: d.name,
    description: d.description ?? undefined,
    basePrice: d.basePrice,
    status: d.status,
    offeringCategoryId: d.offeringCategoryId ?? '',
    discountType: d.discountType ?? undefined,
    discountValue: d.discountValue ?? undefined,
    discountPeriod:
      d.discountStart && d.discountEnd
        ? [dayjs(d.discountStart), dayjs(d.discountEnd)]
        : undefined,
  };
}

const OfferingForm: React.FC<OfferingFormProps> = ({
  form,
  initialValues,
  onFinish,
}) => {
  const [discountType, setDiscountType] = useState<DiscountType | undefined>(undefined);
  const isEdit = initialValues != null;

  const { data: categories = [] } = useOfferingCategoryOptions({ activeOnly: !isEdit });

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(offeringDetailToFormValues(initialValues));
      setDiscountType(initialValues.discountType ?? undefined);
    } else {
      form.resetFields();
      form.setFieldsValue({ status: 0 });
      setDiscountType(undefined);
    }
  }, [initialValues, form]);

  const onValuesChange = (changedValues: Partial<OfferingFormFieldValues>) => {
    if (changedValues.discountType !== undefined) {
      setDiscountType(changedValues.discountType);
    }
  };

  const handleFinish = (v: OfferingFormFieldValues) => {
    const values: OfferingFormValues = {
      ...v,
      discountStart: v.discountPeriod?.[0]?.toISOString(),
      discountEnd: v.discountPeriod?.[1]?.toISOString(),
    };
    delete (values as OfferingFormValues & { discountPeriod?: unknown }).discountPeriod;
    onFinish(values);
  };

  return (
    <Form<OfferingFormFieldValues>
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      onValuesChange={onValuesChange}
    >
      <div className="cp-form-section">
        <Title level={5} style={{ marginTop: 0 }}>
          Basic Information
        </Title>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true, message: 'Name is required' }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="status" label="Status" rules={[{ required: true }]}>
              <Select options={statusOptions} />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item
              name="offeringCategoryId"
              label="Category"
              rules={[{ required: true, message: 'Category is required' }]}
              tooltip="Groups offerings for filters and reporting."
            >
              <Select
                placeholder="Select a category"
                options={categories.map((c) => ({
                  value: c.id,
                  label: c.label,
                }))}
              />
            </Form.Item>
          </Col>
        </Row>
      </div>

      <div className="cp-form-section">
        <Title level={5} style={{ marginTop: 0 }}>
          Pricing
        </Title>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="basePrice"
              label="Base Price"
              rules={[
                { required: true, message: 'Base price is required' },
                { type: 'number', min: 0, message: 'Base price must be positive' },
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                precision={2}
                min={0}
              />
            </Form.Item>
          </Col>
        </Row>
      </div>

      <div className="cp-form-section">
        <Title level={5} style={{ marginTop: 0 }}>
          Discount (Optional)
        </Title>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item name="discountType" label="Discount Type">
              <Select options={discountTypeOptions} allowClear placeholder="None" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="discountValue"
              label="Discount Value"
              rules={[
                {
                  validator: (_, value) => {
                    if (discountType === undefined && value) {
                      return Promise.reject(new Error('Select a discount type first'));
                    }
                    if (discountType !== undefined && !value) {
                      return Promise.reject(new Error('Discount value is required'));
                    }
                    if (discountType === 1 && value > 100) {
                      return Promise.reject(new Error('Percentage cannot exceed 100'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                precision={2}
                suffix={discountType === 1 ? '%' : undefined}
                disabled={discountType === undefined}
              />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item name="discountPeriod" label="Discount Period">
              <DatePicker.RangePicker
                style={{ width: '100%' }}
                disabled={discountType === undefined}
              />
            </Form.Item>
          </Col>
        </Row>
      </div>

      <div className="cp-form-section">
        <Title level={5} style={{ marginTop: 0 }}>
          Additional Details
        </Title>
        <Form.Item name="description" label="Description">
          <TextArea rows={4} />
        </Form.Item>
      </div>
    </Form>
  );
};

export default OfferingForm;
